import { useState, useEffect } from "react";
import { X, Loader2, Download, ZoomIn, ZoomOut } from "lucide-react";
import { Button } from "./ui/button";
import api from "../lib/api";

export default function PDFPreview({ documentId, documentName, onClose }) {
  const [pdfUrl, setPdfUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [zoom, setZoom] = useState(100);

  useEffect(() => {
    loadPDF();
    return () => {
      if (pdfUrl) {
        URL.revokeObjectURL(pdfUrl);
      }
    };
  }, [documentId]);

  const loadPDF = async () => {
    try {
      const response = await api.get(`/documents/${documentId}/preview`, {
        responseType: "blob"
      });
      const url = URL.createObjectURL(response.data);
      setPdfUrl(url);
    } catch (err) {
      setError("Impossible de charger l'aperçu");
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async () => {
    try {
      const response = await api.get(`/documents/${documentId}/download`, {
        responseType: "blob"
      });
      const url = URL.createObjectURL(response.data);
      const link = document.createElement("a");
      link.href = url;
      link.download = documentName;
      link.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Download error:", err);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/80 flex flex-col" data-testid="pdf-preview-modal">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 bg-background border-b">
        <div className="flex items-center gap-4">
          <h3 className="font-medium truncate max-w-md">{documentName}</h3>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setZoom(Math.max(50, zoom - 25))}
              disabled={zoom <= 50}
            >
              <ZoomOut className="w-4 h-4" />
            </Button>
            <span className="text-sm text-muted-foreground w-16 text-center">
              {zoom}%
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setZoom(Math.min(200, zoom + 25))}
              disabled={zoom >= 200}
            >
              <ZoomIn className="w-4 h-4" />
            </Button>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={handleDownload}>
            <Download className="w-4 h-4 mr-2" />
            Télécharger
          </Button>
          <Button variant="ghost" size="icon" onClick={onClose} data-testid="close-preview-btn">
            <X className="w-5 h-5" />
          </Button>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-auto p-4 flex items-start justify-center">
        {loading ? (
          <div className="flex items-center justify-center h-full">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
          </div>
        ) : error ? (
          <div className="text-center text-muted-foreground">
            <p>{error}</p>
          </div>
        ) : (
          <iframe
            src={pdfUrl}
            className="bg-white shadow-xl"
            style={{
              width: `${zoom}%`,
              maxWidth: "100%",
              height: "calc(100vh - 120px)",
              border: "none"
            }}
            title="PDF Preview"
          />
        )}
      </div>
    </div>
  );
}
